import type { BeaconResponse } from '@skyground/vn-dga'
import { generateNonce, verifyBeaconResponse } from '@skyground/vn-dga'

/** The live API origin the client adopts once a beacon earns trust. */
export interface DiscoveredApi {
  domain: string
  /** DGA index of `domain`, so the caller can persist its position and resume from there. */
  index: number
}

/**
 * Everything `discoverApiDomain` needs from the outside world, injected rather than reached
 * for globally so the whole failover-and-trust flow is unit-testable with in-memory fakes and
 * a real ed25519 keypair (see `beacon-client.test.ts`).
 */
export interface DiscoveryEnv {
  /** Ordered candidate `.com` domains to probe (from the DGA). */
  candidates: string[]
  /** Fetch and parse a beacon from a candidate origin. MUST reject on any network failure. */
  fetchBeacon: (url: string) => Promise<BeaconResponse>
  /** Current unix ms, injected so freshness checking stays deterministic in tests. */
  now: () => number
  /** The pinned ed25519 public key — the client's single trust anchor. */
  publicKey: Uint8Array
}

/**
 * Build the beacon URL for a candidate. The path lives under `/_vn/`, which the service worker
 * is required never to cache — the per-request `nonce` challenge only means anything if every
 * probe reaches the network fresh.
 */
export const beaconUrl = (domain: string, nonce: string): string =>
  `https://${domain}/_vn/beacon?nonce=${encodeURIComponent(nonce)}`

/**
 * Walk the DGA candidate list and return the first origin that proves it is genuinely ours.
 *
 * "Proves" means the beacon it returns passes the full pinned-key trust-gate
 * (`verifyBeaconResponse`): signature verifies against the pinned public key, the signed
 * domain is the one we connected to, the echoed nonce is the exact challenge we just issued,
 * and the beacon is fresh. A candidate that merely *responds* — a sinkhole, a block-page, a
 * poisoned IP replaying a stale or forged beacon — is rejected and we move to the next. The
 * trust decision is the signature, never "the domain resolved". Returns `null` if no candidate
 * earns trust.
 */
export const discoverApiDomain = async (env: DiscoveryEnv): Promise<DiscoveredApi | null> => {
  for (const domain of env.candidates) {
    // A fresh, unpredictable challenge per candidate — binds this exchange so a replayed
    // beacon captured from the real origin cannot satisfy a different probe.
    const nonce = generateNonce()

    let response: BeaconResponse
    try {
      response = await env.fetchBeacon(beaconUrl(domain, nonce))
    } catch {
      // Unreachable / sinkholed candidate — nothing to trust, try the next.
      continue
    }

    const result = await verifyBeaconResponse(response, {
      expectedDomain: domain,
      expectedNonce: nonce,
      now: env.now(),
      publicKey: env.publicKey,
    })

    if (result.ok) return { domain: result.payload.domain, index: result.payload.index }
  }

  return null
}
